import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const ARTICLES = [
  {
    id: 'the-art-of-slow',
    category: 'Lifestyle',
    title: 'The Art of Slow',
    excerpt: 'In a world optimized for speed, the most luxurious thing you can do is take your time.',
    date: 'Mar 2026',
    readTime: '4 min read',
    image: 'https://images.unsplash.com/photo-1630175772812-3368aad7982d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjBjYW5kbGUlMjByaXR1YWwlMjBzbW9rZSUyMGF0bW9zcGhlcmljJTIwbHV4dXJ5fGVufDB8fHx8MTc3NDkyMDk1Mnww&ixlib=rb-4.1.0&q=85',
  },
  {
    id: 'what-terpenes-tell-you',
    category: 'Education',
    title: 'What Terpenes Tell You',
    excerpt: 'Beyond THC and CBD: why the aromatic compounds in your flower matter more than you think.',
    date: 'Feb 2026',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1763211840244-085a35c580f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYW5uYWJpcyUyMGxhYiUyMHRlc3RpbmclMjBkYXJrJTIwbW9vZHklMjBzY2llbnRpZmljfGVufDB8fHx8MTc3NDkyMDk0M3ww&ixlib=rb-4.1.0&q=85',
  },
  {
    id: 'night-rituals',
    category: 'Ritual',
    title: 'Night Rituals Worth Keeping',
    excerpt: 'How to build a wind-down routine that turns the end of every day into something you look forward to.',
    date: 'Feb 2026',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1648323140742-85be7a9e7881?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9vZHklMjBjYW5kbGUlMjByaXR1YWwlMjBzbW9rZSUyMGF0bW9zcGhlcmljJTIwbHV4dXJ5fGVufDB8fHx8MTc3NDkyMDk1Mnww&ixlib=rb-4.1.0&q=85',
  },
  {
    id: 'focus-and-flow',
    category: 'Lifestyle',
    title: 'Focus and Flow',
    excerpt: 'The creative case for clarity. How the right experience can sharpen your most important work.',
    date: 'Jan 2026',
    readTime: '4 min read',
    image: 'https://images.unsplash.com/photo-1575980104345-05e59f75736c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxkYXJrJTIwbW9vZHklMjBjYW5kbGUlMjByaXR1YWwlMjBzbW9rZSUyMGF0bW9zcGhlcmljJTIwbHV4dXJ5fGVufDB8fHx8MTc3NDkyMDk1Mnww&ixlib=rb-4.1.0&q=85',
  },
  {
    id: 'less-is-the-point',
    category: 'Philosophy',
    title: 'Less is the Point',
    excerpt: 'Why we release fewer products than our competitors — and why that will never change.',
    date: 'Jan 2026',
    readTime: '3 min read',
    image: 'https://images.unsplash.com/photo-1767500074794-14650311865f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbW9vZHklMjBjYW5kbGUlMjByaXR1YWwlMjBzbW9rZSUyMGF0bW9zcGhlcmljJTIwbHV4dXJ5fGVufDB8fHx8MTc3NDkyMDk1Mnww&ixlib=rb-4.1.0&q=85',
  },
];

const JournalPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Editorial
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="journal-heading"
          >
            The Journal
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            Stories, insights, and perspectives from the world of Secret 13.
          </motion.p>
        </div>
      </section>

      {/* Featured Article */}
      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto"
        >
          <div
            className="group cursor-pointer grid grid-cols-1 md:grid-cols-2 gap-8 border border-white/5 hover:border-white/15 transition-all duration-500"
            data-testid="featured-article"
          >
            <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
              <img
                src={ARTICLES[0].image}
                alt={ARTICLES[0].title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="flex flex-col justify-center p-8 md:p-12">
              <p className="font-sans text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">{ARTICLES[0].category}</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-zinc-50 mb-4 group-hover:text-white transition-colors">
                {ARTICLES[0].title}
              </h2>
              <p className="font-sans text-sm text-zinc-500 leading-relaxed mb-6">{ARTICLES[0].excerpt}</p>
              <div className="flex items-center gap-4">
                <p className="font-sans text-xs text-zinc-600">{ARTICLES[0].date}</p>
                <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                <p className="font-sans text-xs text-zinc-600">{ARTICLES[0].readTime}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Article Grid */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {ARTICLES.slice(1).map((article, index) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            >
              <div className="group cursor-pointer" data-testid={`article-${article.id}`}>
                <div className="aspect-[3/4] overflow-hidden border border-white/5 group-hover:border-white/15 transition-all duration-500 mb-5">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-2">{article.category}</p>
                <h3 className="font-serif text-xl text-zinc-100 mb-2 group-hover:text-white transition-colors">{article.title}</h3>
                <p className="font-sans text-xs text-zinc-500 leading-relaxed mb-3">{article.excerpt}</p>
                <div className="flex items-center gap-3">
                  <p className="font-sans text-[10px] text-zinc-600">{article.date}</p>
                  <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                  <p className="font-sans text-[10px] text-zinc-600">{article.readTime}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default JournalPage;
